import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { ContentTypeForComparison } from 'domains/content/types';
import { URLPaths } from 'models/enums';
import { getContentCreatePath } from 'pages/ContentPage/ContentTable/utils';
import useCreateSms from 'hooks/mutations/useCreateSms';
import useUpdateSms from 'hooks/mutations/useUpdateSms';

type SmsPayload = {
    name: string;
    content: string;
    parentFolderId?: number;
    _saveMode?: 'save' | 'exit';
};

type UseSmsSubmissionProps = {
    id: string | null;
    parentFolderId: number;
    isCreate: boolean;
    isCopy: boolean;
    setErrorMessage: (message: string) => void;
};

const useSmsSubmission = ({ id, parentFolderId, isCreate, isCopy, setErrorMessage }: UseSmsSubmissionProps) => {
    const history = useHistory();
    const queryClient = useQueryClient();

    // Success callback for API operations
    const onSuccess = useCallback(
        (data: any, variables: SmsPayload) => {
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'sms',
            });

            if (isCreate || isCopy) {
                history.replace(
                    getContentCreatePath({
                        contentType: ContentTypeForComparison.SMS,
                        id: data.id,
                        parentFolderId,
                    })
                );
            }

            if (variables._saveMode === 'exit') {
                history.push(`${URLPaths.CONTENT}${parentFolderId ? `/${parentFolderId}` : ''}`);
            }
        },
        [queryClient, isCreate, isCopy, history, parentFolderId]
    );

    // API mutations
    const { mutate: createSms, isLoading: isCreateLoading, error: createError } = useCreateSms({
        onSuccess,
        onError: (error) => {
            setErrorMessage(error.message || 'Failed to save SMS content');
        },
    });

    const { mutate: updateSms, isLoading: isUpdateLoading, error: updateError } = useUpdateSms(id, {
        onSuccess,
        onError: (error) => {
            setErrorMessage(error.message || 'Failed to update SMS content');
        },
    });

    // Save handlers
    const handleSave = useCallback(
        (
            name: string,
            content: string,
            isNameTooShort: boolean,
            isUpdate: boolean,
            saveMode: 'save' | 'exit' = 'save'
        ) => {
            if (!name || isNameTooShort) {
                if (saveMode === 'exit') {
                    setErrorMessage('SMS name must be at least 3 characters');
                }
                return;
            }

            const smsData = {
                name,
                content,
                _saveMode: saveMode,
            };

            if (isUpdate) {
                updateSms(smsData);
            } else {
                const createData = {
                    ...smsData,
                    ...(parentFolderId ? { parentFolderId } : {}),
                };
                createSms(createData);
            }
        },
        [createSms, updateSms, parentFolderId, setErrorMessage]
    );

    return {
        createSms,
        updateSms,
        isCreateLoading,
        isUpdateLoading,
        createError,
        updateError,
        handleSave,
    };
};

export default useSmsSubmission;
